import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Coffee, Leaf, Heart, MapPin, Star, ChevronRight, Sparkles, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import api from '@/utils/apiClient';
import LoyaltySignupForm from '@/components/LoyaltySignupForm';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import MenuItemCard from '@/components/MenuItemCard';
import HomePageSchema from '@/components/StructuredData';
import { CoffeePassHomeHero } from '@/components/CoffeePassPromo';

const iconMap = {
  Coffee: Coffee,
  Leaf: Leaf,
  Heart: Heart
};

// Parse CMS HTML to extract structured content
const parseCMSContent = (html) => {
  if (!html) return {};
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const result = {};
    
    // Extract hero section
    const heroSection = doc.querySelector('.hero-section');
    if (heroSection) {
      result.hero = {
        tagline: heroSection.querySelector('.tagline')?.textContent?.trim() || '',
        title: heroSection.querySelector('h1')?.textContent?.trim() || '',
        description: heroSection.querySelector('p:not(.tagline)')?.textContent?.trim() || '',
      };
    }
    
    // Extract features
    const features = doc.querySelectorAll('.feature');
    if (features.length > 0) {
      result.features = Array.from(features).map(f => ({
        title: f.querySelector('h3')?.textContent?.trim() || '',
        description: f.querySelector('p')?.textContent?.trim() || '',
      }));
    }
    
    // Extract testimonials/blockquotes
    const blockquotes = doc.querySelectorAll('blockquote');
    if (blockquotes.length > 0) {
      result.testimonials = Array.from(blockquotes).map(b => ({
        text: b.querySelector('p')?.textContent?.trim() || '',
        name: b.querySelector('cite')?.textContent?.replace(/^-\s*/, '')?.trim() || '',
      }));
    }
    
    // Extract CTA section
    const ctaSection = doc.querySelector('.cta-section');
    if (ctaSection) {
      result.cta = {
        title: ctaSection.querySelector('h2')?.textContent?.trim() || '',
        description: ctaSection.querySelector('p')?.textContent?.trim() || '',
      };
    }
    
    return result;
  } catch (e) {
    console.error('Error parsing CMS content:', e);
    return {};
  }
};

export default function HomePage() {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const { addToCart } = useCart();
  
  // Today's Specials state
  const [specials, setSpecials] = useState(null);
  const [currentSpecialIndex, setCurrentSpecialIndex] = useState(0);
  const [isSpecialExpanded, setIsSpecialExpanded] = useState(false);
  
  // Add-ons for MenuItemCard
  const [availableAddOns, setAvailableAddOns] = useState([]);
  
  // CMS Content state
  const [cmsContent, setCmsContent] = useState({});

  // Fetch CMS content for home page
  useEffect(() => {
    api.get('/simple-cms/pages/home')
      .then(res => {
        const parsed = parseCMSContent(res.data.content);
        setCmsContent(parsed);
      })
      .catch(err => console.error('Failed to fetch CMS content:', err));
  }, []);

  // Get content from CMS with fallbacks
  const hero = cmsContent.hero || {};
  const cmsFeatures = cmsContent.features || [];
  const cmsTestimonials = cmsContent.testimonials || [];
  const cta = cmsContent.cta || {};

  useEffect(() => {
    const cacheBuster = `?_t=${Date.now()}`;
    api.get(`/menu/featured${cacheBuster}`)
      .then(res => setFeaturedItems(res.data.slice(0, 4)))
      .catch(console.error);
    
    // Fetch today's specials
    api.get(`/specials${cacheBuster}`)
      .then(res => {
        if (res.data.enabled && res.data.items?.length > 0) {
          setSpecials(res.data);
        }
      })
      .catch(console.error);
    
    // Fetch add-ons for MenuItemCard
    api.get('/add-ons')
      .then(res => setAvailableAddOns(res.data))
      .catch(console.error);
  }, []);

  // Auto-rotate specials every 5 seconds (pause when expanded)
  useEffect(() => {
    if (!specials?.items?.length || isSpecialExpanded) return;
    
    const interval = setInterval(() => {
      setCurrentSpecialIndex(prev => 
        prev === specials.items.length - 1 ? 0 : prev + 1
      );
    }, 5000); // 5 seconds rotation

    return () => clearInterval(interval);
  }, [specials, isSpecialExpanded]);

  const handleAddSpecialToCart = (item) => {
    addToCart(item);
    toast.success(`${item.name} added to cart!`);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await api.post('/newsletter/subscribe', { email });
      toast.success('Welcome to the Bulaccino Cafe family!');
      setEmail('');
    } catch {
      toast.error('Subscription failed. Please try again.');
    }
    setSubscribing(false);
  };

  return (
    <div className="overflow-hidden">
      <SEO {...SEO_CONFIG.home} path="/" />
      <HomePageSchema />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/2074130/pexels-photo-2074130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260" 
            alt="Bulaccino Cafe - Fiji Island Coffee Shop" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 pt-40">
          <div className="max-w-2xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-primary font-medium mb-4 flex items-center gap-2"
            >
              <span className="w-8 h-px bg-primary"></span>
              {hero.tagline || "Welcome to Fiji's Favorite Café"}
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl text-foreground leading-tight mb-6"
            >
              {hero.title ? (
                <>{hero.title}</>
              ) : (
                <>Where Island Soul<span className="text-primary"> Meets</span> Coffee Culture</>
              )}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl"
            >
              {hero.description || "Escape to Bulaccino Cafe — your tropical sanctuary in Nadi. Fresh island flavors, handcrafted coffee, and the warmth of Fijian hospitality await."}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/menu" data-testid="hero-view-menu-btn">
                <Button className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6 text-base shadow-lg shadow-primary/20 btn-shine">
                  View Menu
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/order" data-testid="hero-order-btn">
                <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary hover:bg-primary/5 px-8 py-6 text-base">
                  Order Online
                </Button>
              </Link>
            </motion.div>

            {/* Quick Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex gap-8 mt-12 pt-8 border-t border-border/50"
            >
              {(hero.stats || [
                { value: "7AM", label: "Open Daily" },
                { value: "100%", label: "Fresh & Local" },
                { value: "5★", label: "Rated" }
              ]).map((stat, i) => (
                <div key={i}>
                  <p className="font-heading text-3xl text-primary">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-10 right-10 hidden lg:block">
          <div className="w-32 h-32 bg-accent/20 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Today's Specials Banner */}
      {specials?.enabled && specials?.items?.length > 0 && (
        <section 
          className="py-12 md:py-16 relative"
          style={{
            background: specials.background_style === 'gradient' 
              ? 'linear-gradient(135deg, hsl(142, 76%, 20%) 0%, hsl(var(--accent)) 100%)'
              : 'hsl(var(--primary))'
          }}
          data-testid="todays-specials-section"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          </div>

          <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4"
              >
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-white/90 text-sm font-medium">Limited Time</span>
              </motion.div>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-2">
                {specials.title || "Today's Specials"}
              </h2>
              <p className="text-white/80 max-w-lg mx-auto">
                {specials.subtitle || "Fresh picks rotating throughout the day"}
              </p>
            </div>

            {/* Single Special Item Display - Full Width Like Island Favorites */}
            <div className="max-w-md mx-auto relative z-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSpecialIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`special-item-${specials.items[currentSpecialIndex]?.id}`}
                >
                  <MenuItemCard 
                    item={specials.items[currentSpecialIndex]} 
                    availableAddOns={availableAddOns}
                    onExpandChange={setIsSpecialExpanded}
                  />
                </motion.div>
              </AnimatePresence>
              
              {/* Dots Indicator */}
              {specials.items.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {specials.items.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSpecialIndex(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentSpecialIndex 
                          ? 'bg-white w-6' 
                          : 'bg-white/40 hover:bg-white/60'
                      }`}
                      data-testid={`special-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* View All Link */}
            <div className="text-center mt-8">
              <Link to="/menu" data-testid="specials-view-menu-btn">
                <Button 
                  variant="outline" 
                  className="rounded-full border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50"
                >
                  View Full Menu
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Bula Coffee Pass Hero Section */}
      <CoffeePassHomeHero />

      {/* Featured Items Section */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-primary font-medium mb-3">Taste the Difference</p>
            <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
              Island Favorites
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From our signature Bulaccino Cafe blend to tropical breakfast bowls, discover what keeps locals and travelers coming back.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredItems.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`featured-item-${item.id}`}
              >
                <MenuItemCard 
                  item={item} 
                  availableAddOns={availableAddOns}
                />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/menu" data-testid="view-full-menu-btn">
              <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary px-8">
                View Full Menu
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Bulaccino Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-primary font-medium mb-3">The Bulaccino Cafe Difference</p>
              <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-6">
                More Than Just a Café
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Bulaccino Cafe isn't just about coffee — it's a celebration of Fiji's vibrant culture and natural abundance. Every cup we brew and every dish we serve tells the story of our islands.
              </p>

              <div className="space-y-6">
                {(cmsFeatures.length > 0 ? cmsFeatures : [
                  { icon: "Coffee", title: "Premium Coffee", description: "Locally roasted beans, expertly crafted into the perfect cup every time." },
                  { icon: "Leaf", title: "Fresh & Local", description: "Farm to cup freshness with ingredients sourced from Fiji's finest farms." },
                  { icon: "Heart", title: "Island Hospitality", description: "Warm Fijian service that makes every visit feel like coming home." }
                ]).map((item, i) => {
                  const IconComponent = iconMap[item.icon] || Coffee;
                  return (
                    <motion.div
                      key={item.title || i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      viewport={{ once: true }}
                      className="flex gap-4"
                    >
                      <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <Link to="/about" className="inline-block mt-8" data-testid="learn-more-btn">
                <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary">
                  Learn Our Story
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <img 
                  src="https://images.pexels.com/photos/906136/pexels-photo-906136.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Bulaccino Cafe"
                  className="w-full rounded-3xl shadow-2xl"
                />
              </motion.div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Live Cafe Stream Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-primary font-medium mb-3">Live from Bulaccino</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-4">
              See What's Happening Right Now
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Take a peek inside our cafe! Watch the live stream and see our team crafting your favorite drinks.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative w-full max-w-4xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl border border-border"
          >
            <iframe 
              width="100%" 
              height="100%" 
              src="https://www.youtube.com/embed/pwJjhEpfWak?si=QSS9aOySgWXp8TO4&controls=0" 
              title="Bulaccino Cafe Live Stream" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
              className="absolute inset-0"
            />
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-primary font-medium mb-3">What People Say</p>
            <h2 className="font-heading text-4xl md:text-5xl text-foreground">
              Loved by Locals & Travelers
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(cmsTestimonials.length > 0 ? cmsTestimonials : [
              { name: "Sarah M., Regular Customer", text: "The best acai bowl I've ever had! The flavors are incredible and the atmosphere is so relaxing.", rating: 5 },
              { name: "James T., Coffee Enthusiast", text: "Finally, a café that does coffee right. The Bulaccino blend is now my daily ritual.", rating: 5 },
              { name: "Emma K., Travel Blogger", text: "A hidden gem in Nadi! Perfect for a quick breakfast or a long afternoon with friends.", rating: 5 }
            ]).map((testimonial, i) => (
              <motion.div
                key={testimonial.name || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="testimonial-card bg-card p-8 rounded-3xl shadow-lg"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-foreground/80 leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                <p className="font-medium text-primary">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary font-medium mb-3">Find Us</p>
              <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-6">
                Your Tropical Escape Awaits
              </h2>
              <div className="flex items-start gap-4 mb-6">
                <MapPin className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-lg">Shop 3 at Concave Road</p>
                  <p className="text-muted-foreground">Namaka, Nadi, Fiji</p>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Nestled in the heart of Nadi, Bulaccino Cafe is just minutes from the airport — the perfect first or last stop on your Fiji adventure.
              </p>
              <Link to="/contact" data-testid="get-directions-btn">
                <Button className="rounded-full bg-primary text-white hover:bg-primary/90 px-8">
                  Get Directions
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="map-container h-80 lg:h-96">
              <iframe
                title="Bulaccino Cafe Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3909.8!2d177.4475!3d-17.7633!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sShop+3+Concave+Road%2C+Namaka%2C+Nadi%2C+Fiji!5e0!3m2!1sen!2sfj!4v1600000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Program Signup Section */}
      <section className="py-24 md:py-32 newsletter-bg text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h2 className="font-heading text-4xl md:text-5xl mb-4">
                Join The Loyalty Rewards Program
              </h2>
              <p className="text-white/80 mb-6 text-lg">
                Sign up and get 10% off every order for your first 30 days! Plus earn free coffees, 
                VIP status, and a special treat on your birthday.
              </p>
              <ul className="space-y-3 text-white/70">
                <li className="flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-accent" />
                  <span><strong className="text-white">Free WiFi</strong> — exclusive member access</span>
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-accent" />
                  <span>10% discount for first 30 days</span>
                </li>
                <li className="flex items-center gap-2">
                  <Coffee className="w-5 h-5 text-accent" />
                  <span>Buy 10 coffees, get 1 free</span>
                </li>
                <li className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-accent" />
                  <span>Free coffee on your birthday</span>
                </li>
              </ul>
            </div>
            <LoyaltySignupForm variant="homepage" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
            {cta.title || "Ready for a Taste of Paradise?"}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            {cta.description || "Whether you're starting your day or taking a tropical break, Bulaccino Cafe is waiting for you."}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/order" data-testid="final-cta-order-btn">
              <Button className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6 text-base shadow-lg shadow-primary/20">
                Order Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="final-cta-visit-btn">
              <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary px-8 py-6 text-base">
                Visit Us Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
